import { useState, useCallback, useContext, ReactElement } from 'react';
import styled from 'styled-components';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Tooltip, Typography } from '@mui/material';
import { Flex, FlexColumn } from '../../Flex';
import IconButton from '../../IconButton';
import WidgetButton from '../../WidgetButton';
import { TrackingContext } from '../../../providers/tracking';
import {
  ApprovalSubmissionResult,
  OnUserInteraction,
  ToolInvocationEvent,
  ToolRendererFunction,
} from '../types';
import { downloadTableAsCSV } from '../utils';
import {
  previewUiContextUpdate,
  requiresUiContextApproval,
} from '../uiContext';
import {
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  space,
} from '../../../style/utils';
import FEToolRenderer, {
  hasFEToolRenderer,
  isFEToolExpandable,
} from './FEToolRenderer';
import { getApprovalToolRenderer } from './ApprovalToolRenderer';
import PageUpdateApprovalCard, {
  getProposedPageUpdate,
} from './PageUpdateApprovalCard';
import ToolCallChip, { stringify } from './ToolCallChip';
import { formatToolName, ToolText } from './toolDisplay';

const ICON_AREA_WIDTH = 32;

const ToolFullWidth = styled.div`
  width: 90cqi;
  margin-left: calc((100% - ${ICON_AREA_WIDTH}px - 90cqi) / 2);
  margin-right: calc((100% + ${ICON_AREA_WIDTH}px - 90cqi) / 2);
`;

const ErrorBox = styled(FlexColumn)`
  gap: ${space('smMinus')};
  padding: ${space('sm', 'md')};
  border: 1px solid ${({ theme }) => theme.palette.status.error.main};
  border-radius: ${radius('lg')};
  background: ${({ theme }) => theme.palette.surface.hover};
  max-width: 100%;
`;

const ErrorMessage = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ${fontFamily('monoSystem')};
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.status.error.main};
  max-height: 300px;
  overflow: auto;
`;

const NextSteps = styled.span`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.muted};
  white-space: pre-wrap;
  word-break: break-word;
`;

const getToolErrorMessage = (
  result: ToolInvocationEvent['toolInvocation']['result'],
): string => {
  if (!result) return 'Tool call failed.';
  if (typeof result.error === 'string' && result.error.trim()) {
    return result.error;
  }
  if (result.result !== undefined) return stringify(result.result);
  return stringify(result);
};

type FEToolSectionProps = {
  invocation: ToolInvocationEvent['toolInvocation'];
  contentMaxWidth?: number;
  onUserInteraction?: OnUserInteraction;
  isToolLoading: boolean;
  interactionsDisabled?: boolean;
};

const FEToolSection = ({
  invocation,
  contentMaxWidth,
  onUserInteraction,
  isToolLoading,
  interactionsDisabled,
}: FEToolSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const tracking = useContext(TrackingContext);

  const isExpandable = isFEToolExpandable(invocation.toolName || '');
  const isTable = invocation.toolName === 'table_display';
  const showToolbar = isExpandable && (isTable || !!contentMaxWidth);

  const handleDownload = () => {
    downloadTableAsCSV(
      invocation.args?.rows || [],
      invocation.args?.columns || [],
    );
    tracking?.sendTrackingEvent({
      eventName: tracking.analytics.events.AI_CHAT_TABLE_CSV_DOWNLOAD,
    });
  };

  const content = (
    <>
      {showToolbar && (
        <Flex sx={{ justifyContent: 'flex-end', mb: '4px' }}>
          {isTable && (
            <Tooltip title="Export CSV">
              <IconButton onClick={handleDownload}>
                <DownloadRoundedIcon sx={{ fontSize: fontSize('xl') }} />
              </IconButton>
            </Tooltip>
          )}
          {!!contentMaxWidth && (
            <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
              <IconButton onClick={() => setIsExpanded((prev) => !prev)}>
                {isExpanded ? (
                  <CloseFullscreenRoundedIcon
                    sx={{ fontSize: fontSize('lg') }}
                  />
                ) : (
                  <OpenInFullRoundedIcon sx={{ fontSize: fontSize('lg') }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      )}
      <FEToolRenderer
        toolName={invocation.toolName}
        toolArgs={invocation.args}
        onUserInteraction={onUserInteraction}
        selectedValue={invocation.result}
        isActive={isToolLoading}
        interactionsDisabled={interactionsDisabled}
      />
    </>
  );

  if (isExpandable && isExpanded && contentMaxWidth) {
    return <ToolFullWidth>{content}</ToolFullWidth>;
  }

  return content;
};

type Props = {
  invocation: ToolInvocationEvent['toolInvocation'];
  isDebug?: boolean;
  onApproveToolUse?: (
    val: boolean,
    interruptId?: string,
    newArgs?: Record<string, unknown>,
  ) => Promise<ApprovalSubmissionResult>;
  onApplyFrontendTool?: (
    toolCallId: string,
    toolName: string,
    args: Record<string, unknown>,
  ) => void | Promise<void>;
  onRejectFrontendTool?: (toolCallId: string) => void | Promise<void>;
  onUserInteraction?: OnUserInteraction;
  interactionsDisabled?: boolean;
  getToolRenderer?: (toolName?: string) => ToolRendererFunction | null;
  contentMaxWidth?: number;
  showToolErrorDetails?: boolean;
};

const ToolInvocationRenderer = ({
  invocation,
  isDebug = false,
  onApproveToolUse,
  onApplyFrontendTool,
  onRejectFrontendTool,
  onUserInteraction,
  interactionsDisabled,
  getToolRenderer,
  contentMaxWidth,
  showToolErrorDetails = false,
}: Props) => {
  const [userResponded, setUserResponded] = useState(false);

  const wrapFullWidth = (element: ReactElement) =>
    contentMaxWidth ? <ToolFullWidth>{element}</ToolFullWidth> : element;

  const isToolLoading = invocation.state === 'call';
  const isToolError = invocation.result?.isError;
  const isToolApproval = invocation.type === 'approval';
  const isFETool = hasFEToolRenderer(invocation.toolName);
  const isStartEvent = invocation.type === 'start';

  const approveWithInterruptId = useCallback(
    (approved: boolean, newArgs?: Record<string, unknown>) =>
      onApproveToolUse?.(approved, invocation.interruptId, newArgs) ??
      Promise.resolve<ApprovalSubmissionResult>('not_ready'),
    [onApproveToolUse, invocation.interruptId],
  );

  const tryRenderWithToolRenderer = (
    renderer: ToolRendererFunction | null,
  ): ReactElement | null => {
    if (!renderer) {
      return null;
    }
    return (
      renderer(
        invocation.toolName || '',
        invocation.result?.result,
        invocation.args,
        isToolLoading,
        onUserInteraction,
      ) || null
    );
  };

  if (isDebug) {
    return (
      <FlexColumn>
        <Flex sx={{ gap: 1 }}>
          <Typography sx={{ fontWeight: fontWeight('semibold') }}>
            {invocation.toolName}
          </Typography>

          {isToolLoading && <Typography>Running...</Typography>}
        </Flex>

        <FlexColumn>
          <Flex sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: fontWeight('semibold') }}>
              Input:
            </Typography>

            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(invocation.args, null, 2)}
            </Typography>
          </Flex>

          <Flex sx={{ gap: 1 }}>
            <Typography sx={{ fontWeight: fontWeight('semibold') }}>
              Output:
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {typeof invocation.result === 'object'
                ? JSON.stringify(invocation.result, null, 2)
                : String(invocation.result)}
            </Typography>
          </Flex>
        </FlexColumn>
      </FlexColumn>
    );
  }

  const customApprovalRenderer = getApprovalToolRenderer(invocation.toolName);
  if (customApprovalRenderer) {
    const rendered = customApprovalRenderer({
      invocation,
      onApproveToolUse: approveWithInterruptId,
      onUserInteraction,
      isActive: isToolLoading,
      interactionsDisabled: interactionsDisabled || isStartEvent,
    });
    if (rendered) {
      return rendered;
    }
  }

  if (isToolApproval) {
    return (
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        <ToolText>{invocation.message || 'Do you approve tool use?'}</ToolText>
        <WidgetButton
          sx={{ minWidth: 0 }}
          variant="outlined"
          disabled={userResponded || interactionsDisabled}
          onClick={() => {
            void approveWithInterruptId(true).then((result) => {
              if (result === 'delivered') setUserResponded(true);
            });
          }}
        >
          Yes
        </WidgetButton>
        <WidgetButton
          sx={{ minWidth: 0 }}
          variant="outlined"
          disabled={userResponded || interactionsDisabled}
          onClick={() => {
            void approveWithInterruptId(false).then((result) => {
              if (result === 'delivered') setUserResponded(true);
            });
          }}
        >
          No
        </WidgetButton>
      </Flex>
    );
  }

  if (isToolError && showToolErrorDetails && !isStartEvent) {
    const nextSteps = invocation.result?.nextSteps;
    return wrapFullWidth(
      <ErrorBox>
        <Flex sx={{ alignItems: 'center', gap: 1 }}>
          <ErrorRoundedIcon
            sx={{
              fontSize: fontSize('xl'),
              color: 'status.error.main',
              flexShrink: 0,
            }}
          />
          <ToolText sx={{ color: 'status.error.main' }}>
            {formatToolName(invocation.toolName)} failed
          </ToolText>
        </Flex>
        <ErrorMessage>{getToolErrorMessage(invocation.result)}</ErrorMessage>
        {nextSteps && <NextSteps>{nextSteps}</NextSteps>}
      </ErrorBox>,
    );
  }

  if (!isStartEvent && getToolRenderer) {
    const rendered = tryRenderWithToolRenderer(
      getToolRenderer(invocation.toolName),
    );
    if (rendered) {
      return wrapFullWidth(rendered);
    }
  }

  if (isToolError) {
    return (
      <Flex sx={{ alignItems: 'center', gap: 1 }}>
        <ErrorRoundedIcon
          sx={{ fontSize: fontSize('xl'), color: 'accent.ai' }}
        />
        <ToolText>Tool call failure</ToolText>
      </Flex>
    );
  }

  const toolName = invocation.toolName;
  if (
    invocation.type === 'output' &&
    invocation.state === 'call' &&
    toolName &&
    requiresUiContextApproval(toolName)
  ) {
    const proposed = getProposedPageUpdate(invocation.args);
    return (
      <PageUpdateApprovalCard
        rows={previewUiContextUpdate(toolName, proposed)}
        args={invocation.args}
        isDisabled={interactionsDisabled}
        onApply={() =>
          onApplyFrontendTool?.(
            invocation.toolCallId,
            toolName,
            invocation.args ?? {},
          )
        }
        onReject={() => onRejectFrontendTool?.(invocation.toolCallId)}
      />
    );
  }

  if (
    isFETool &&
    (!isStartEvent || invocation.toolName === 'user_actions_display')
  ) {
    return (
      <FEToolSection
        invocation={invocation}
        contentMaxWidth={contentMaxWidth}
        onUserInteraction={onUserInteraction}
        isToolLoading={isToolLoading}
        interactionsDisabled={interactionsDisabled || isStartEvent}
      />
    );
  }

  if (getToolRenderer) {
    const rendered = tryRenderWithToolRenderer(getToolRenderer());
    if (rendered) {
      return wrapFullWidth(rendered);
    }
  }

  if (isFETool || isStartEvent) {
    const goalText = (invocation.args?.goal as string | undefined)?.trim();
    const toolText = isFETool
      ? isToolLoading
        ? 'Thinking...'
        : 'Done!'
      : goalText || formatToolName(invocation.toolName);

    return (
      <Flex sx={{ alignItems: 'center', gap: 1 }}>
        {isToolLoading ? (
          <SearchRoundedIcon
            sx={{ fontSize: fontSize('xl'), color: 'accent.ai' }}
          />
        ) : (
          <CheckCircleRoundedIcon
            sx={{ fontSize: fontSize('xl'), color: 'accent.ai' }}
          />
        )}
        {toolText && <ToolText $running={isToolLoading}>{toolText}</ToolText>}
      </Flex>
    );
  }

  return <ToolCallChip invocation={invocation} />;
};

export default ToolInvocationRenderer;
